import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, gql, NetworkStatus } from '@apollo/client';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from './AuthContext';
import './office_panel.css';
import './consultas_ia.css';
import CredencialesIntegrador from './CredencialesIntegrador';
import HistorialPreguntas from './HistorialPreguntas';
import UsuariosApi from './UsuariosApi';
import EmpresasApi from './EmpresasApi';
import MonitorApi from './MonitorApi';

// Consultas a la IA de MCP_NAWI en el ambiente elegido (preproduccion: api-pre.nawigpt.com). Solo superadmin.
// Desde el 3/11/2026 la pestaña Monitor muestra cada pedido que recibió la API, explicado, y se actualiza cada 5 minutos.
// La API aplica el tope; esta pantalla solo lee el contador y el registro de uso.
const GET_CONSULTAS_IA = gql`
  query GetConsultasIA($dia: String, $dias: Int, $entorno: String) {
    getConsultasIA(dia: $dia, dias: $dias, entorno: $entorno) {
      entorno base zonaHoraria dia hoy consultas fuente topeTotal topeUsuario actualizado error errorMensaje
      usuarios {
        userId email nombre consultas tope mensajes topicos informes
        bloqueados fallidos cancelados tokensEntrada tokensSalida ultima
      }
      historial {
        dia consultas fuente mensajes topicos informes bloqueados fallidos usuarios tokensEntrada tokensSalida
      }
    }
  }
`;

const DIAS_HISTORIAL = 14;
const fmtNum = (n) => new Intl.NumberFormat('es-AR').format(n || 0);
const fmtDia = (d, opts = { weekday: 'short', day: '2-digit', month: '2-digit' }) =>
  new Date(`${d}T12:00:00Z`).toLocaleDateString('es-AR', { ...opts, timeZone: 'UTC' });
const fmtHora = (iso, tz) =>
  new Date(iso).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', timeZone: tz });
const plural = (n, uno, varios) => `${fmtNum(n)} ${n === 1 ? uno : varios}`;

function Barra({ valor, tope }) {
  const pct = tope ? Math.min(100, Math.round((valor * 100) / tope)) : 0;
  const nivel = !tope ? 'sin' : pct >= 100 ? 'lleno' : pct >= 80 ? 'alto' : 'ok';
  return (
    <div className={`cia-barra cia-barra--${nivel}`} role="progressbar"
      aria-valuenow={valor} aria-valuemin={0} aria-valuemax={tope || undefined}>
      <span style={{ width: `${pct}%` }} />
    </div>
  );
}

function AvisoError({ codigo, mensaje }) {
  return (
    <div className="panel-card cia-aviso">
      <h2>{codigo === 'sin_permiso' ? 'Falta un permiso en Atlas' : 'No se pudieron leer las consultas'}</h2>
      <p>{mensaje}</p>
      {codigo === 'sin_permiso' && (
        <ol>
          <li>En MongoDB Atlas, entrar a <b>Database Access</b>.</li>
          <li>Editar el usuario de base de datos que usa NAWI Office.</li>
          <li>Agregar el rol integrado <b>read</b> sobre la base <b>Nawi3_preprod</b> y guardar.</li>
          <li>Volver a esta pantalla y tocar <b>Actualizar</b>. No hace falta reiniciar nada.</li>
        </ol>
      )}
    </div>
  );
}

// Pantalla "API" del superadmin: una pestaña por ambiente (API Producción, API Preproducción) y, adentro, Usuarios API,
// Consultas, Historial y Credenciales de ese ambiente. Un ambiente inactivo se ve pero no se usa.
const GET_ENTORNOS = gql`query { getEntornosApi { clave nombre activo url } }`;
const AMBIENTES = [['prod', 'API Producción'], ['pre', 'API Preproducción']];
const SECCIONES = [['empresas', 'Empresas'], ['usuarios', 'Usuarios API'], ['consultas', 'Consultas'], ['historial', 'Historial'], ['monitor', 'Monitor'], ['credenciales', 'Credenciales']];

export default function ConsultasIA() {
  const { isSuperadmin, logout } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { data: dEnt } = useQuery(GET_ENTORNOS, { skip: !isSuperadmin, fetchPolicy: 'cache-and-network' });
  const amb = params.get('amb') === 'prod' ? 'prod' : 'pre';
  const pestana = SECCIONES.some(([k]) => k === params.get('tab')) ? params.get('tab') : 'empresas';
  const entorno = (dEnt?.getEntornosApi || []).find((e) => e.clave === amb);
  const host = entorno?.url ? entorno.url.replace(/^https?:\/\//, '').replace(/\/.*$/, '') : null;

  if (!isSuperadmin) {
    return (
      <div className="page-container" style={{ maxWidth: 900 }}>
        <div className="panel-card" style={{ padding: 40, textAlign: 'center' }}>
          <h2 style={{ fontSize: '1.2rem', fontWeight: 700, marginBottom: 8 }}>Acceso denegado</h2>
          <p style={{ color: '#6b7280', marginBottom: 16 }}>Solo el superadmin puede acceder.</p>
          <button className="btn-accent" onClick={() => navigate('/altaEmpresa')}>Volver</button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container cia" style={{ maxWidth: 1100 }}>
      <div className="office-header">
        <div className="office-header-left">
          <button className="btn-subtle" onClick={() => navigate('/altaEmpresa')}>← Volver</button>
          <h1 style={{ fontSize: '1.3rem', fontWeight: 700, letterSpacing: '-0.3px', margin: 0 }}>API para integradores</h1>
          {host && <span className="tag tag--purple">{host}</span>}
        </div>
        <button className="btn-subtle" onClick={() => logout()}>Cerrar sesión</button>
      </div>

      <div className="cia-ambientes" role="tablist" aria-label="Ambiente">
        {AMBIENTES.map(([k, t]) => {
          const e = (dEnt?.getEntornosApi || []).find((x) => x.clave === k);
          return (
            <button key={k} role="tab" aria-selected={amb === k}
              className={`cia-ambiente ${amb === k ? 'cia-ambiente--activo' : ''} ${e && !e.activo ? 'cia-ambiente--apagado' : ''}`}
              onClick={() => setParams(k === 'pre' ? {} : { amb: k })}>
              {t}{e && !e.activo && <span className="cia-ambiente-estado">todavía no activo</span>}
            </button>
          );
        })}
      </div>

      {entorno && !entorno.activo ? (
        <div className="panel-card cia-inactivo">
          <h2>El entorno de {entorno.nombre.toLowerCase()} de la API todavía no está activo</h2>
          <p>Cuando exista, esta pestaña va a administrarlo igual que la de preproducción: empresas, usuarios API, consultas, historial y credenciales.
            Se activa sola cuando el servidor de Office tenga cargados los datos de conexión de ese entorno.</p>
        </div>
      ) : (
        <>
          <div className="cia-tabs" role="tablist">
            {SECCIONES.map(([k, t]) => (
              <button key={k} role="tab" aria-selected={pestana === k}
                className={`cia-tab ${pestana === k ? 'cia-tab--activa' : ''}`}
                onClick={() => setParams({ ...(amb === 'pre' ? {} : { amb }), ...(k === 'empresas' ? {} : { tab: k }) })}>{t}</button>
            ))}
          </div>
          {entorno && (
            <>
              {pestana === 'empresas' && <EmpresasApi key={amb} entorno={amb} />}
              {pestana === 'usuarios' && <UsuariosApi key={amb} entorno={amb} />}
              {pestana === 'consultas' && <PanelConsultas key={amb} entorno={amb} />}
              {pestana === 'historial' && <HistorialPreguntas key={amb} entorno={amb} />}
              {pestana === 'monitor' && <MonitorApi key={amb} entorno={amb} />}
              {pestana === 'credenciales' && <CredencialesIntegrador key={amb} entorno={amb} />}
            </>
          )}
        </>
      )}
    </div>
  );
}

function PanelConsultas({ entorno }) {
  const [dia, setDia] = useState(null);   // null = hoy, en la zona del corte diario

  const { data, loading, error, refetch, networkStatus } = useQuery(GET_CONSULTAS_IA, {
    variables: { dia, dias: DIAS_HISTORIAL, entorno },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });
  const r = data?.getConsultasIA;
  const esHoy = !dia || (r && r.dia === r.hoy);

  // El dia en curso se refresca solo cada minuto.
  useEffect(() => {
    if (!esHoy) return undefined;
    const t = setInterval(() => refetch(), 60000);
    return () => clearInterval(t);
  }, [esHoy, refetch]);

  const totales = useMemo(() => {
    const t = { mensajes: 0, topicos: 0, informes: 0, bloqueados: 0, fallidos: 0, tokensEntrada: 0, tokensSalida: 0 };
    (r?.usuarios || []).forEach((u) => Object.keys(t).forEach((k) => { t[k] += u[k] || 0; }));
    return t;
  }, [r]);

  const actualizando = networkStatus === NetworkStatus.refetch || networkStatus === NetworkStatus.setVariables;
  const quedan = r && r.topeTotal ? Math.max(0, r.topeTotal - r.consultas) : null;

  return (
    <>
      <div className="panel-card cia-barra-herramientas">
        <label className="cia-campo">
          <span>Día</span>
          <input type="date" value={r?.dia || ''} max={r?.hoy || undefined}
            onChange={(e) => setDia(e.target.value || null)} />
        </label>
        <button className="btn-outline" disabled={esHoy} onClick={() => setDia(null)}>Hoy</button>
        <button className="btn-accent" disabled={actualizando || loading} onClick={() => refetch()}>
          {actualizando ? 'Actualizando…' : 'Actualizar'}
        </button>
        <span className="cia-nota">
          {r ? `Leído a las ${fmtHora(r.actualizado, r.zonaHoraria)}` : ''}
          {r && esHoy ? ' · se actualiza solo cada minuto' : ''}
        </span>
      </div>

      {loading && !r && <div className="spinner" />}
      {error && <AvisoError codigo="error" mensaje={error.message} />}
      {r?.error && <AvisoError codigo={r.error} mensaje={r.errorMensaje} />}

      {r && !r.error && (
        <>
          <div className="cia-kpis">
            <div className="panel-card cia-kpi cia-kpi--principal">
              <span className="cia-kpi-titulo">Consultas del {esHoy ? 'día' : fmtDia(r.dia, { day: '2-digit', month: '2-digit', year: 'numeric' })}</span>
              <div className="cia-kpi-valor">
                {fmtNum(r.consultas)}
                {r.topeTotal ? <small> de {fmtNum(r.topeTotal)}</small> : null}
              </div>
              <Barra valor={r.consultas} tope={r.topeTotal} />
              <span className="cia-kpi-pie">
                {quedan === null ? 'Tope total desconocido' : quedan === 0 ? 'Tope total alcanzado' : `Quedan ${plural(quedan, 'consulta', 'consultas')}`}
                {r.fuente === 'registro' ? ' · estimado desde el registro' : ''}
              </span>
            </div>
            <div className="panel-card cia-kpi">
              <span className="cia-kpi-titulo">Evaluadores con consultas</span>
              <div className="cia-kpi-valor">{fmtNum(r.usuarios.length)}</div>
              <span className="cia-kpi-pie">{r.topeUsuario ? `Tope de ${fmtNum(r.topeUsuario)} por evaluador` : 'Tope por evaluador desconocido'}</span>
            </div>
            <div className="panel-card cia-kpi">
              <span className="cia-kpi-titulo">Por tipo</span>
              <ul className="cia-tipos">
                <li><b>{fmtNum(totales.mensajes)}</b> mensajes</li>
                <li><b>{fmtNum(totales.topicos)}</b> tópicos</li>
                <li><b>{fmtNum(totales.informes)}</b> informes</li>
              </ul>
              {(totales.bloqueados > 0 || totales.fallidos > 0) && (
                <span className="cia-kpi-pie">
                  {totales.bloqueados > 0 ? plural(totales.bloqueados, 'rechazado', 'rechazados') : ''}
                  {totales.bloqueados > 0 && totales.fallidos > 0 ? ' · ' : ''}
                  {totales.fallidos > 0 ? plural(totales.fallidos, 'fallido', 'fallidos') : ''}
                </span>
              )}
            </div>
            <div className="panel-card cia-kpi">
              <span className="cia-kpi-titulo">Tokens</span>
              <ul className="cia-tipos">
                <li><b>{fmtNum(totales.tokensEntrada)}</b> de entrada</li>
                <li><b>{fmtNum(totales.tokensSalida)}</b> de salida</li>
              </ul>
            </div>
          </div>

          <div className="panel-card cia-seccion">
            <div className="section-head">
              <span className="section-title">Por evaluador</span>
              <span className="section-count">{r.usuarios.length}</span>
            </div>
            {r.usuarios.length === 0 ? (
              <div className="empty-state">Sin consultas {esHoy ? 'hoy' : 'ese día'}.</div>
            ) : (
              <div className="cia-usuarios">
                {r.usuarios.map((u) => (
                  <div key={u.userId} className="cia-usuario">
                    <div className="cia-usuario-quien">
                      <span className="cia-usuario-email">{u.email || 'Usuario sin datos'}</span>
                      <span className="cia-usuario-nombre">{u.nombre || u.userId}</span>
                    </div>
                    <div className="cia-usuario-uso">
                      <div className="cia-usuario-numeros">
                        <b>{fmtNum(u.consultas)}</b>{u.tope ? ` de ${fmtNum(u.tope)}` : ''}
                        {u.tope && u.consultas >= u.tope ? <span className="tag tag--red">tope alcanzado</span> : null}
                      </div>
                      <Barra valor={u.consultas} tope={u.tope} />
                    </div>
                    <div className="cia-usuario-detalle">
                      <span className="tag tag--blue">{plural(u.mensajes, 'mensaje', 'mensajes')}</span>
                      {u.topicos > 0 && <span className="tag tag--purple">{plural(u.topicos, 'tópico', 'tópicos')}</span>}
                      {u.informes > 0 && <span className="tag tag--green">{plural(u.informes, 'informe', 'informes')}</span>}
                      {u.bloqueados > 0 && <span className="tag tag--yellow">{plural(u.bloqueados, 'rechazado', 'rechazados')}</span>}
                      {u.fallidos > 0 && <span className="tag tag--red">{plural(u.fallidos, 'fallido', 'fallidos')}</span>}
                    </div>
                    <div className="cia-usuario-ultima">
                      {u.ultima ? `Última ${fmtHora(u.ultima, r.zonaHoraria)}` : ''}
                      <span>{fmtNum(u.tokensEntrada + u.tokensSalida)} tokens</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="panel-card cia-seccion">
            <div className="section-head">
              <span className="section-title">Últimos {DIAS_HISTORIAL} días</span>
              <span className="cia-nota">Tocá un día para ver su detalle</span>
            </div>
            <div className="cia-tabla-scroll">
              <table className="cia-tabla">
                <thead>
                  <tr>
                    <th>Día</th><th className="num">Consultas</th><th className="num">Mensajes</th>
                    <th className="num">Tópicos</th><th className="num">Informes</th><th className="num">Rechazados</th>
                    <th className="num">Fallidos</th><th className="num">Evaluadores</th><th className="num">Tokens</th>
                  </tr>
                </thead>
                <tbody>
                  {r.historial.map((h) => (
                    <tr key={h.dia} className={h.dia === r.dia ? 'cia-fila--elegida' : ''}
                      onClick={() => setDia(h.dia === r.hoy ? null : h.dia)} tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter') setDia(h.dia === r.hoy ? null : h.dia); }}>
                      <td>{h.dia === r.hoy ? 'Hoy' : fmtDia(h.dia)}</td>
                      <td className="num">
                        <b>{fmtNum(h.consultas)}</b>
                        {h.fuente === 'registro' && h.consultas > 0 && (
                          <span className="cia-estimado" title="Estimado desde el registro de uso: el contador diario se conserva 3 días">≈</span>
                        )}
                      </td>
                      <td className="num">{fmtNum(h.mensajes)}</td>
                      <td className="num">{fmtNum(h.topicos)}</td>
                      <td className="num">{fmtNum(h.informes)}</td>
                      <td className="num">{fmtNum(h.bloqueados)}</td>
                      <td className="num">{fmtNum(h.fallidos)}</td>
                      <td className="num">{fmtNum(h.usuarios)}</td>
                      <td className="num">{fmtNum(h.tokensEntrada + h.tokensSalida)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <p className="cia-pie">
            Fuente: base {r.base} de MCP_NAWI, entorno {r.entorno}. El día se corta a la medianoche de Buenos Aires.
            Mensajes, tópicos e informes descuentan del mismo contador; los mensajes rechazados por políticas de uso no cuentan.
            El tope lo aplica la API; esta pantalla solo lo muestra.
          </p>
        </>
      )}
    </>
  );
}
